import React, { useState, useRef, useEffect } from 'react';
import { kaReducer, Table } from 'ka-table';
import {
  ICellTextProps,
  ITableProps,
  IDataRowProps,
  ICellProps } from
'ka-table/props';
import {
  hideLoading,
  showLoading,
  updateData,
  hideDetailsRow,
  showDetailsRow } from
'ka-table/actionCreators';
import { Column } from 'ka-table/models';
import { DispatchFunc } from 'ka-table/types';
import ReactToPrint from 'react-to-print';
import cx from 'classnames';
import defaultOptions from 'ka-table/defaultOptions';

import { serverEmulator, getCostForAddon } from '../../../util';
import {
  Button,
  Icon,
  Input,
  COLOURS,
  Text } from
'../../../../../ui-kit';

import BadgeStatus from '../../../../Addons/components/AddonManagementModal/components/BadgeStatus';
import InvoiceButton from './InvoiceButton';

import 'ka-table/style.css';
import '../styles/_organisation-audit-log-table.scss';

const LOAD_MORE_DATA = 'LOAD_MORE_DATA';
const INITIAL_LOAD = 'INITIAL_LOAD';

const tablePropsInit = {
  rowKeyField: 'id'
};

const INITIAL_DETAILS_ATTRIBUTES = {
  buttonAttributes: {},
  elementAttributes: {
    style: {
      backgroundColor: COLOURS.mono.offwhite35
    }
  }
};



























const DetailsButton = ({
  dispatch,
  rowKeyValue,
  isDetailsRowShown,
  value,
  rowData,
  onShowDetailsClick
}) => {
  const { showProjectOutputHistory, showDetails } = rowData;
  return (
    <div className="detailed-dropdown">
      <span className="detailed-dropdown__value">{value}</span>
      {showDetails &&
      <Icon
        name="info"
        size={15}
        color="nevada"
        onClick={() => onShowDetailsClick && onShowDetailsClick(rowData)} />}



      {showProjectOutputHistory &&
      <Icon
        name={!isDetailsRowShown ? 'arrowdown' : 'arrowup'}
        size={10}
        onClick={() => {
          dispatch(
            isDetailsRowShown ?
            hideDetailsRow(rowKeyValue) :
            showDetailsRow(rowKeyValue)
          );
        }} />}


    </div>);

};

const PaymentStatus = ({ status }) => {
  const badgeTypes = {
    PAID: { type: 'PRIMARY', labelText: 'Paid' },
    REFUNDED: { type: 'SECONDARY', labelText: 'Refunded' }
  };

  const badge = badgeTypes[status];

  if (!badge) return null;

  return (
    <div className="status-badge">
      <BadgeStatus {...badge} />
    </div>);

};

const ProjectStatus = ({ status }) => {
  const badgeTypes = {
    ACTIVE: { type: 'PRIMARY', labelText: 'Active' },
    DELETED: { type: 'WARNING', labelText: 'Deleted' }
  };

  const badge = badgeTypes[status];

  if (!badge) return null;

  return (
    <div className="status-badge">
      <BadgeStatus {...badge} />
    </div>);

};

const getCredits = (creditPlus, creditMinus) => {
  if (creditMinus) return `-${creditMinus}`;
  if (creditPlus) return `+${creditPlus}`;

  return null;
};

const ResponsiveDataRow = ({
  rowData,
  columns,
  dispatch,
  isDetailsRowShown,
  rowKeyValue,
  isDetailsComponent,
  onShowDetailsClick
}) => {
  const {
    type,
    showProjectOutputHistory,
    showDetails = false,
    createdAt,
    description,
    creditPlus,
    creditMinus
  } = rowData;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <td
      className={cx(defaultOptions.css.cell, 'audit-log-table__responsive-row')}
      colSpan={columns.length}
      {...{
        onClick: () => showProjectOutputHistory && isDetailsComponent ?
        dispatch(
          isDetailsRowShown ?
          hideDetailsRow(rowKeyValue) :
          showDetailsRow(rowKeyValue)
        ) :
        void 0
      }}>

      <div className="audit-log-table__responsive-row__item first-item">
        <Text body className="bold-text">
          {type}
          {showDetails &&
          <Icon
            name="info"
            size={15}
            color="nevada"
            onClick={() => onShowDetailsClick && onShowDetailsClick(rowData)}
            className="audit-log-table__responsive-row__icon" />}


        </Text>
        {' '}
        <Text body className="item-row-data">
          {createdAt}
        </Text>
      </div>
      <div className="audit-log-table__responsive-row__item second-item">
        <Text body>{description ? description.message : ''}</Text>
        <Text body>{getCredits(creditPlus, creditMinus)}</Text>
      </div>
    </td>);

};

const OrganisationAuditLogsTable = ({
  dataArray,
  getPaymentInvoice,
  columns = [],
  infiniteScroll = false,
  detailsRows = void 0,
  detailsComponent,
  detailsComponentAttributes,
  showPrintButton = false,
  PrintButtonText = 'Click to Print',
  showClientSideSearch = false,
  printButtonProps,
  isMobileView,
  showTableSummary,
  onShowDetailsClick,
  isShootstaAdmin,
  maxHeight,
  isLoading,
  ...extraTableProps
}) => {
  const {
    buttonAttributes,
    elementAttributes
  } = detailsComponentAttributes || INITIAL_DETAILS_ATTRIBUTES;

  const componentRef = useRef(null);
  const [tableProps, changeTableProps] = useState({
    ...tablePropsInit,
    ...(infiniteScroll ? {
      virtualScrolling: {
        enabled: true
      }
    } : {}),
    columns
  });
  const [pageIndex, changePageIndex] = useState(0);
  const [searchText, setSearchText] = useState('');

  const dispatch = async (action) => {
    changeTableProps((prevState) => kaReducer(prevState, action));

    if (!infiniteScroll) {
      changeTableProps((prevState) => ({
        ...prevState,
        data: dataArray
      }));
    }

    if (infiniteScroll && pageIndex !== -1 && action.type === LOAD_MORE_DATA) {
      dispatch(showLoading());
      const result = await serverEmulator.get(pageIndex, dataArray);
      changePageIndex(result.pageIndex);
      dispatch(updateData([...(tableProps.data || []), ...result.data]));
      dispatch(hideLoading());
      return;
    }

    if (infiniteScroll && pageIndex === 0 && action.type === INITIAL_LOAD) {
      dispatch(showLoading());
      const result = await serverEmulator.get(pageIndex, dataArray);
      changePageIndex(result.pageIndex);
      dispatch(updateData(result.data || []));
      dispatch(hideLoading());
    }
  };

  useEffect(() => {
    if (!dataArray) return;
    changePageIndex(0);
    changeTableProps((prevState) => ({
      ...prevState,
      singleAction: { type: INITIAL_LOAD },
      noData: {
        text: 'No Data Found'
      }
    }));
  }, [dataArray]);

  return (
    <div
      className={cx('audit-log-table', {
        'audit-log-table__small': isMobileView
      })}>

      {showPrintButton || showClientSideSearch ?
      <div className="audit-log-table__header">
          {showPrintButton &&
        <div className="audit-log-table__print-button">
              <ReactToPrint
            trigger={() =>
            <Button {...printButtonProps}>{PrintButtonText}</Button>}

            content={() => componentRef.current} />

            </div>}

          {showClientSideSearch &&
        <Input
          type="search"
          placeholder="Search Table..."
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          className="audit-log-table__search-input" />}


        </div> :
      null}
      <div ref={componentRef} className="audit-log-table__content">
        <Table
          dispatch={dispatch}
          format={({ value }) => <Text ellipsisOverflow>{value}</Text>}
          detailsRows={detailsRows}
          loading={{
            enabled: isLoading,
            text: 'Loading data, please wait...'
          }}
          childComponents={{
            headRow: isMobileView && {
              content: (props) => <div {...props} />
            },
            dataRow: {
              content: (props) => isMobileView &&
              <ResponsiveDataRow
                {...props}
                isDetailsComponent={Boolean(detailsComponent)}
                onShowDetailsClick={onShowDetailsClick} />


            },
            cell: {
              elementAttributes: ({ column }) => ({
                'data-column': column.title
              })
            },
            tableWrapper: infiniteScroll && {
              elementAttributes: () => ({
                onScroll: (event, { baseFunc }) => {
                  baseFunc(event);
                  const element = event.currentTarget;
                  if (
                  element.offsetHeight + element.scrollTop >=
                  element.scrollHeight)
                  {
                    dispatch({ type: LOAD_MORE_DATA });
                  }
                },
                style: { maxHeight: maxHeight || 600 }
              })
            },
            cellText: {
              content: (callProps) => {
                const {
                  column: { key },
                  rowData
                } = callProps;
                const { position = 'no' } = buttonAttributes || {};
                switch (key) {
                  case position:
                    return (
                      <DetailsButton
                        {...callProps}
                        onShowDetailsClick={onShowDetailsClick} />);


                  case 'createdBy':
                    return rowData.createdBy ? rowData.createdBy.firstName : '';
                  case 'invoiceId':
                    return (
                      <InvoiceButton
                        invoiceId={rowData.invoiceId}
                        getPaymentInvoice={getPaymentInvoice} />);


                  case 'paymentStatus':
                    return <PaymentStatus status={rowData.paymentStatus} />;
                  case 'isProjectDeleted':
                    return (
                      <>
                        {rowData.showDetails ?
                        <ProjectStatus status={rowData.isProjectDeleted ? 'DELETED' : 'ACTIVE'} /> :
                        null}

                      </>);

                  default:
                    return null;
                }
              }
            },
            detailsRow: detailsComponent && {
              elementAttributes: () => elementAttributes,
              content: detailsComponent
            },
            summaryCell: showTableSummary && {
              content: ({ column, data }) => {
                switch (column.key) {
                  case 'description.message':
                    return <b>Total</b>;
                  case 'creditPlus':{
                      const sum = data.reduce((accumulator, currentValue) => {
                        // remove asterisk from string and convert to number
                        const creditPlus = currentValue.creditPlus ?
                        Number(currentValue.creditPlus.replace(/\*/g, '')) :
                        0;

                        return accumulator + creditPlus;
                      }, 0);
                      return <b>{Math.abs(sum)}</b>;
                    }
                  case 'creditMinus':{
                      const sum = data.reduce((accumulator, currentValue) => {
                        const creditMinus = currentValue.creditMinus ?
                        Number(currentValue.creditMinus.replace(/\*/g, '')) :
                        0;

                        return accumulator + creditMinus;
                      }, 0);
                      return <b>{Math.abs(sum)}</b>;
                    }
                  case 'addonsCost.withCurrency':{
                      const sum = data.reduce(
                        (accumulator, currentValue) => {
                          const {
                            numberValue,
                            billingEntityCode
                          } = currentValue.addonsCost;

                          if (!billingEntityCode || !numberValue) {
                            return accumulator;
                          }

                          return {
                            cost: accumulator.cost + numberValue,
                            billingEntityCode
                          };
                        },
                        { cost: 0, billingEntityCode: 'AUS' }
                      );

                      const { withCurrency } = getCostForAddon(sum);

                      return <b>{withCurrency}</b>;
                    }
                  default:
                    return null;
                }
              }
            }
          }}
          searchText={searchText}
          {...tableProps}
          {...extraTableProps} />

      </div>
    </div>);

};
export default OrganisationAuditLogsTable;